import React, { useState } from "react";
import { Lock, ShieldCheck, AlertTriangle, Command } from "lucide-react";

export default function PasswordGate({ isOpen, onSuccess, onClose, accessKey }) {
  const [status, setStatus] = useState("locked"); // locked | denied
  const [input, setInput] = useState("");
  const [shake, setShake] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    const key = accessKey || process.env.REACT_APP_REPORT_ACCESS_KEY;
    if (input === key) {
      setStatus("locked");
      setInput("");
      onSuccess();
    } else {
      setStatus("denied");
      setShake(true);
      setTimeout(() => setShake(false), 600);
    }
  };

  const handleClose = () => {
    setStatus("locked");
    setInput("");
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-950/80 backdrop-blur-xl">
      {/* Ambient glow */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-indigo-500/5 blur-3xl" />
      </div>

      {/* Clickable backdrop */}
      <div className="absolute inset-0" onClick={handleClose} />

      <div
        className={`relative w-full max-w-md mx-4 p-8 rounded-2xl bg-slate-900/90 border border-white/10 shadow-2xl shadow-indigo-500/10 transition-transform ${
          shake ? "animate-[shake_0.5s_ease-in-out]" : ""
        }`}
      >
        {/* Header */}
        <div className="flex flex-col items-center text-center mb-8">
          <div className="w-14 h-14 rounded-xl bg-indigo-600 flex items-center justify-center shadow-lg shadow-indigo-500/30 mb-5">
            <Command className="text-white w-7 h-7" />
          </div>
          <h2 className="text-xl font-black text-white tracking-tight mb-1">
            Restricted Access
          </h2>
          <p className="text-xs text-slate-500 font-medium uppercase tracking-widest">
            Frontier Intelligence • Classified Report
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <Lock
              size={14}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500"
            />
            <input
              type="password"
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                if (status === "denied") setStatus("locked");
              }}
              placeholder="Enter access key"
              autoFocus
              className="w-full pl-10 pr-4 py-3 rounded-xl bg-slate-800/80 border border-white/10 text-white text-sm font-medium placeholder-slate-600 focus:outline-none focus:border-indigo-500/50 focus:ring-1 focus:ring-indigo-500/30 transition-all"
            />
          </div>

          <button
            type="submit"
            className="w-full py-3 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-bold uppercase tracking-widest transition-all shadow-lg shadow-indigo-500/20 hover:shadow-indigo-500/40 flex items-center justify-center gap-2"
          >
            <ShieldCheck size={14} />
            Authenticate
          </button>
        </form>

        {/* Denied State */}
        {status === "denied" && (
          <div className="mt-5 p-4 rounded-xl bg-red-500/10 border border-red-500/20 flex items-start gap-3">
            <AlertTriangle
              size={16}
              className="text-red-400 mt-0.5 flex-shrink-0"
            />
            <div>
              <p className="text-sm font-bold text-red-400">Access Denied</p>
              <p className="text-xs text-red-400/70 mt-0.5">
                Wrong access key. Please contact your administrator for
                authorization.
              </p>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-6 pt-4 border-t border-white/5 text-center">
          <p className="text-[10px] text-slate-600 font-bold uppercase tracking-widest">
            Encrypted • Zero Retention • Sovereign
          </p>
        </div>
      </div>

      {/* Shake keyframe */}
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-8px); }
          40% { transform: translateX(8px); }
          60% { transform: translateX(-6px); }
          80% { transform: translateX(6px); }
        }
      `}</style>
    </div>
  );
}
